/**
 * @file        src/modals/FlightLoadingModal.jsx
 * @description Transient "loading flights" overlay. Premium card with the Beyond
 *              Destination wordmark, a route (origin ✈ destination), a 0–100%
 *              progress runway the plane advances along, soft glow, animated dots
 *              and a rotating reassurance line. Route / dates / pax reflect the
 *              real search. Shown while the /air-search query is in flight.
 *
 *              THE PERCENTAGE IS MODELLED, NOT MEASURED — see the PROGRESS MODEL
 *              block below before changing it. Its one hard rule: the timer may
 *              never reach 100%, because only a finished search means finished.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-12 Claude — Percentage readout MOVED: it was right-aligned above the
 *                          runway, hanging over the end of the track, where it read
 *                          as a label on the finish line rather than on the search.
 *                          It now sits CENTRED between the runway and the bouncing
 *                          dots, a size larger. The clearance from the dots is
 *                          explicit and not interchangeable with the old spacing —
 *                          the dots bounce upward into whatever sits above them.
 *   - 2026-09-12 Claude — Flown portion of the runway is SOLID (3px, rounded, soft
 *                          coral glow) instead of the old dashed trail, so the bar
 *                          reads the same way neon's does. Dashed-over-dashed meant
 *                          the covered distance looked identical to the distance
 *                          still to go — a fill you cannot see is not a fill.
 *   - 2026-09-12 Claude — Ported Neon's PROGRESS MODEL: the runway is now the
 *                          progress bar. Beyond showed no percentage at all — the
 *                          plane looped on an `nhFly` timer and the coral trail on
 *                          `nhTrail`, both independent of the actual search, so the
 *                          customer got motion but no sense of how far along they
 *                          were. The plane now sits AT the percentage with the coral
 *                          trail filled behind it, a 0–100% readout sits over the end
 *                          of the runway, and the card stays up through a watchable
 *                          finish sweep. Brand colours unchanged (Horizon Teal plane,
 *                          Jet Ink endpoints, Passport Coral trail, Stone Grey dots).
 *                          Carries over every fix Neon's version accumulated: the
 *                          render-phase visibility (no unmount flash before the
 *                          finish plays), the per-run `key` remount (no backwards
 *                          sweep on a modified search), `runKey` to tell a new search
 *                          from an `open` flicker, and the monotonic ramp.
 *   - 2026-06-22 Claude — Initial port. No backdrop click — the parent
 *                         flight-search flow closes this on its own timer,
 *                         but Esc still escapes for safety.
 *   - 2026-06-25 Claude — Accept from/to/when/pax props; shown live while the
 *                         /air-search query is in flight.
 *   - 2026-06-29 Claude — Redesigned: Neon wordmark, gliding-plane runway,
 *                         neon glow, rotating status line.
 *   - 2026-07-03 Claude — Route separator now a direction plane (was the ⇄
 *                         swap-arrows glyph, which read as a round-trip toggle).
 *   - 2026-07-06 Claude — Route separator reflects trip type: double arrow (⇌)
 *                         for round trip, single arrow (→) for one-way (via the
 *                         new `roundTrip` prop). Runway plane below unchanged.
 *   - 2026-07-27 Claude — Brand colour guide: gliding plane now Horizon Teal (#29A8BE)
 *                         and the three status dots Stone Grey (#6E7780).
 *   - 2026-07-27 Claude — Runway endpoint dots and the route direction arrow also
 *                         recoloured to Jet Ink (#1C1C1C) to match the plane.
 *   - 2026-07-27 Claude — Added a highlight trail that fills the runway line in sync
 *                         with the gliding plane and resets on each loop, so the line
 *                         lights up as the plane passes and repeats. Trail is Passport
 *                         Coral (#E8687D) and reuses the grey track's 8px/16px dash
 *                         pattern + position so its dashes align exactly over the line.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import NeonWordmark from '../components/NeonWordmark.jsx';
import { LOGO_SIZE } from '../components/BrandLogo.jsx';

const TIPS = [
  'Loading the best deals across 500+ airlines…',
  'Comparing fares, baggage and cancellation rules…',
  'Finding the fastest routes and lowest prices…',
  'Locking in live availability for your dates…',
];

// ---- PROGRESS MODEL -------------------------------------------------------
// /air-search is ONE request with no intermediate signal — it is pending, then it
// is done — and nearly all of the wait is the supplier's, not the download's. So
// there is no true percentage to read; this is a deliberate ESTIMATE, built to be
// honest about the one thing that matters: it must never claim to be finished
// before the results actually are.
//
// The curve is a QUADRATIC ease-out over a fixed ramp, driven by elapsed time —
// roughly 15%/s down to 3%/s, decelerating but evenly enough to read as one
// continuous movement rather than a sprint followed by a stall.
//
// Time-based, not per-tick-increment: the position is derived from the elapsed
// milliseconds every tick, so a throttled background tab or a dropped frame
// cannot make the bar drift behind where it should be.
const CEIL = 90;      // the timer's hard ceiling — only a real result passes it
const TICK = 60;      // ms between updates: smooth without thrashing React
const RAMP_MS = 11000; // time to coast from 0 to CEIL; then it waits there
// FINISHING BEAT — deliberately unhurried. A search typically lands somewhere
// around 50–70%, so the run home is a THIRD of the track; covered in a couple of
// hundred milliseconds it does not read as motion at all, and the customer sees
// "70%", a blink, and the results. Given long enough to be watched, the same
// distance reads as the search completing. Bounded to about a second all told,
// since it does hold back results the customer already has.
const FINISH_MS = 700;   // sweep from wherever the ramp reached up to 100
const SWEEP_TICK = 25;   // ms; ~28 steps across the sweep, so the digits COUNT up
const HOLD_MS = 300;     // 100% on screen before the card goes

// Ease-out for the sweep: quick off the mark, settling into 100 rather than
// slamming into it. Linear arrived at full speed and stopped dead.
const easeOut = (x) => 1 - (1 - x) * (1 - x);

// Quadratic ease-out, 0 → CEIL across RAMP_MS, flat at CEIL after.
const rampAt = (elapsed) => {
  const x = Math.min(elapsed / RAMP_MS, 1);
  return CEIL * (1 - (1 - x) * (1 - x));
};

export default function FlightLoadingModal({
  open,
  onClose,
  from = '',
  to = '',
  when = '',
  pax = '',
  roundTrip = false,
  // Identifies WHICH search is running (the results page passes flightSearch.seq,
  // which is bumped on every Search press and keys the queries themselves). It is
  // what tells a genuinely new search — reset to 0 — apart from `open` merely
  // flickering false for a render on the same search, which must not reset
  // anything. Without it both looked identical from in here.
  runKey = null,
}) {
  const [tip, setTip] = useState(0);
  const [pct, setPct] = useState(0);
  // Bumped per search. Used as a React `key` on the trail and the plane so a reset
  // REMOUNTS them: a fresh node renders at 0 with nothing to transition from,
  // whereas setting width 100% → 0% on the existing node made the bar sweep
  // visibly BACKWARDS (and the plane fly right-to-left) at the start of the next
  // search — the "loader goes in reverse" glitch.
  const [run, setRun] = useState(0);
  // Mirrors `pct` so the finish can read where the ramp had got to. State would
  // be stale inside the effect that starts the sweep.
  const pctRef = useRef(0);
  // When THIS run's ramp began. Held in a ref, not recreated per effect run, so a
  // flicker resumes the same climb instead of restarting it from zero.
  const startedAtRef = useRef(0);
  const lastRunRef = useRef(null);
  const put = (v) => { pctRef.current = v; setPct(v); };

  // ---- VISIBILITY IS DERIVED DURING RENDER, NOT IN AN EFFECT ----------------
  //
  // This is what stops "the bar reached 80%, the results appeared, and THEN the
  // last 20% played". With visibility as `open || outro` and `outro` set by an
  // effect, effects run AFTER the render commits — so the moment `open` goes
  // false there is one committed frame where the card returns null: it unmounts,
  // the browser paints the results behind it, and only then does the effect bring
  // the card back to finish.
  //
  // Setting state during render is React's documented way to adjust state when a
  // prop changes: React discards this render pass and immediately re-renders with
  // the new phase BEFORE committing anything, so the null return is never painted.
  // BOTH seeds must come from `open`. This modal is mounted by the results page,
  // which arrives with its query ALREADY fetching — so on one-way and round trip
  // the very first render has open=true, and seeding phase to 'idle' while waiting
  // for a false→true transition would mean the loader never appears at all for
  // those trip types.
  const [phase, setPhase] = useState(open ? 'running' : 'idle');
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setPhase('running');
    else if (phase === 'running') setPhase('finishing');
  }

  useEffect(() => {
    if (phase === 'running') {
      const isNewRun = runKey !== lastRunRef.current || !startedAtRef.current;
      lastRunRef.current = runKey;
      if (isNewRun) {
        startedAtRef.current = Date.now();
        setRun((r) => r + 1);
        put(0);
      }
      const id = setInterval(() => {
        const next = rampAt(Date.now() - startedAtRef.current);
        // MONOTONIC. If `open` flickered mid-search the finish sweep will have
        // nudged the bar ahead of the ramp; recomputing from elapsed time would
        // then drag it back down. A progress bar may stall, but it may never
        // retreat — so hold the high-water mark until the ramp catches up.
        put(Math.max(pctRef.current, next));
      }, TICK);
      return () => clearInterval(id);
    }
    if (phase === 'finishing') {
      // `startPct`, not `from` — that name is taken by the origin-city prop.
      const startPct = pctRef.current;
      const startedAt = Date.now();
      const sweep = setInterval(() => {
        const x = Math.min((Date.now() - startedAt) / FINISH_MS, 1);
        put(startPct + (100 - startPct) * easeOut(x));
        if (x >= 1) clearInterval(sweep);
      }, SWEEP_TICK);
      const done = setTimeout(() => {
        setPhase('idle');
        // This run is over. Clearing the start time makes the NEXT open a new run
        // even if it somehow arrives carrying the same runKey.
        startedAtRef.current = 0;
      }, FINISH_MS + HOLD_MS);
      return () => { clearInterval(sweep); clearTimeout(done); };
    }
    return undefined;
  }, [phase, runKey]);

  useEffect(() => {
    // Keyed on `phase`, not `open`: the page must stay locked through the finish
    // too, or it unlocks (and can jump) while the card is still covering it.
    if (phase === 'idle') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const rot = setInterval(() => setTip((t) => (t + 1) % TIPS.length), 1800);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      clearInterval(rot);
    };
  }, [phase, onClose]);

  // The card covers the results for the WHOLE run, finish included.
  if (phase === 'idle') return null;

  const meta = [when, pax].filter(Boolean).join(' · ');
  const shown = Math.round(pct);

  // Portaled to <body> so it shares the ROOT stacking context with the airline /
  // traveller popovers (also portaled). Without this the modal renders inside the
  // results page's nested stacking context and its z-index can't beat the
  // popovers' — they'd stay visible on top of the loader.
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Dim + blur backdrop. /95 rather than /85: the results ARE rendered behind
          this for the whole finish (the page stops loading the moment the query
          lands, whatever the bar says), and at 85% enough of them showed through
          to read as "the results arrived before the loader finished". */}
      <div className="absolute inset-0 bg-night/95 backdrop-blur-md" />

      {/* card */}
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-neon/20 bg-panel/95 px-8 pt-7 pb-8 text-center shadow-[0_30px_80px_-20px_rgba(18,121,140,.35)]">
        {/* ambient neon glows */}
        <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-72 -translate-x-1/2 rounded-full bg-neon/20 blur-[90px]" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -right-10 h-48 w-48 rounded-full bg-sky/15 blur-[80px]" aria-hidden="true" />

        <div className="relative">
          {/* brand */}
          <NeonWordmark className={'mx-auto ' + LOGO_SIZE + ' text-neon drop-shadow-[0_0_18px_rgba(18,121,140,.45)]'} />

          {/* route */}
          <div className="mt-6 flex items-center justify-center gap-3 font-display text-[22px] font-extrabold leading-none">
            <span>{from || 'Origin'}</span>
            {/* Direction glyph reflects the trip type: a double arrow (⇌) for a
                round trip, a single arrow (→) for one-way. */}
            {roundTrip ? (
              <svg viewBox="0 0 24 24" className="w-6 h-5 text-[#1C1C1C] shrink-0"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 7h15m-4-4 4 4-4 4M20 17H5m4-4-4 4 4 4" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="w-6 h-5 text-[#1C1C1C] shrink-0"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 12h15m-5-5 5 5-5 5" /></svg>
            )}
            <span>{to || 'Destination'}</span>
          </div>
          {meta && <p className="mt-2 text-muted text-sm">{meta}</p>}

          {/* THE RUNWAY IS THE PROGRESS BAR. The plane no longer loops on a timer
              independent of anything — it sits AT the percentage, the coral trail
              fills the track behind it, and the grey dashes ahead of it are the
              distance still to go. One metaphor doing one job, instead of an
              animation that said nothing. */}
          <div className="mx-auto mt-6 w-full max-w-[300px]">
            {/* The percentage no longer sits up here — it is centred below the
                runway, above the dots. */}
            <div
              className="relative h-10"
              role="progressbar"
              aria-valuenow={shown}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Searching flights"
            >
              {/* dashed track — the part not yet flown */}
              <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-[repeating-linear-gradient(90deg,rgba(148,163,184,.45)_0_8px,transparent_8px_16px)]" />
              {/* flown portion — SOLID, not the old dashed trail. Once the trail
                  stopped being a decorative loop and became the progress itself, the
                  dashes worked against it: dashed-over-dashed left the covered
                  distance reading as the same broken line as the distance still to
                  go, so there was no "filled" to see. Solid + a 3px weight + a soft
                  glow is the same read as neon's, in Passport Coral (#E8687D) rather
                  than neon green. `transition` on width rather than a shorter TICK:
                  the interval only has to be fast enough to feed the transition, and
                  the browser interpolates the rest on the compositor — smooth without
                  re-rendering React every frame. */}
              <div
                key={`trail-${run}`}
                className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-gradient-to-r from-[#E8687D] to-[#F2909F] shadow-[0_0_12px_rgba(232,104,125,.6)] transition-[width] duration-150 ease-linear"
                style={{ width: `${pct}%` }}
              />
              {/* endpoints — Jet Ink #1C1C1C to match the route arrow. The far one
                  fills in only when the search has genuinely finished. */}
              <span className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-[#1C1C1C]" />
              <span
                className={'absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border-2 border-[#1C1C1C] transition-colors duration-200 '
                  + (shown >= 100 ? 'bg-[#1C1C1C]' : '')}
              />
              {/* plane — Horizon Teal #29A8BE (brand colour guide), parked at the
                  current percentage. No bob: a vertical wobble on something that is
                  also a progress indicator made the position look imprecise. */}
              <div
                key={`plane-${run}`}
                className="absolute top-1/2 transition-[left] duration-150 ease-linear"
                style={{ left: `${pct}%`, transform: 'translate(-50%, -50%)' }}
              >
                <span className="relative flex items-center justify-center">
                  <span className="absolute h-9 w-9 rounded-full bg-[#29A8BE]/25 blur-md" />
                  <svg viewBox="0 0 24 24" className="relative w-6 h-6 rotate-90 text-[#29A8BE] drop-shadow-[0_2px_6px_rgba(41,168,190,.55)]">
                    <path fill="currentColor" d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* THE PERCENTAGE READOUT — centred on the card, between the runway and
              the dots, in Horizon Teal to match the plane it reports on. The gaps
              either side are deliberate: the dots BOUNCE (translateY -25% of their
              own height), so they intrude upward into whatever sits above them.
              Splitting the old single mt-5 into mt-4 + mt-4 keeps the number clear
              of the dots at the top of that bounce instead of letting the two read
              as one blob.

              tabular-nums: proportional digits change width as the number climbs,
              which makes a centred readout twitch left and right on every tick. */}
          <p className="mt-4 text-center font-display text-[18px] font-extrabold leading-none tabular-nums text-[#29A8BE]">{shown}%</p>

          {/* bouncing dots — Stone Grey #6E7780 (brand colour guide) */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#6E7780] animate-bounce" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#6E7780] animate-bounce" style={{ animationDelay: '.15s' }} />
            <span className="w-2.5 h-2.5 rounded-full bg-[#6E7780] animate-bounce" style={{ animationDelay: '.3s' }} />
          </div>

          {/* rotating reassurance line */}
          <p key={tip} className="mt-4 text-sm text-ink/90 animate-[fade_.4s_ease]">
            {TIPS[tip].replace(/500\+ airlines…?/, '')}
            {tip === 0 && <span className="text-neon font-semibold">500+ airlines…</span>}
          </p>
        </div>
      </div>

      {/* Local keyframes. `nhFly` (which looped `left` 0→100% independently of any
          real state) and `nhTrail` (its matching width loop) are both gone — `left`
          and `width` are the progress now. Only the status line's fade is left. */}
      <style>{`
        @keyframes fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      `}</style>
    </div>,
    document.body,
  );
}
